import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    InputAdornment, Chip, Divider, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress,
    Alert, CardMedia, useTheme, IconButton, Tooltip, Menu, LinearProgress, Fade
} from '@mui/material';
import {
    Send, ArrowBack, NotificationsActive, Link, Image as ImageIcon,
    Warning, PhoneAndroid, Title, CheckCircle, Info,
    FormatBold, EmojiEmotions, AutoAwesome
} from '@mui/icons-material';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
// Import the service we created earlier
import { sendBroadcastNotification } from '../services/notificationService';
import { BroadcastPayload } from '../types/Notification';

// --- CONSTANTS ---
const COMMON_DEEPLINKS = [
    { label: 'Home', value: 'apnayatri://home' },
    { label: 'Promo Page', value: 'apnayatri://promo' },
    { label: 'Ride History', value: 'apnayatri://history' },
    { label: 'Referral', value: 'apnayatri://referral' },
];

const NotificationComposer: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const bodyInputRef = useRef<HTMLInputElement>(null);

    // --- STATE ---
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [result, setResult] = useState<{ open: boolean, message: string, count: number } | null>(null);

    // Emoji Menu State
    const [emojiAnchorEl, setEmojiAnchorEl] = useState<null | HTMLElement>(null);

    const [form, setForm] = useState<BroadcastPayload>({
        target: 'rider', // Default
        title: '',
        body: '',
        imageUrl: '',
        deeplink: '',
        isUrgent: false,
        data: {}
    });

    // --- HANDLERS ---
    const handleChange = (field: keyof BroadcastPayload, value: any) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleDeeplinkChip = (link: string) => {
        setForm(prev => ({ ...prev, deeplink: link }));
    };

    const handleSendClick = () => {
        if (!form.title || !form.body) return;
        setConfirmOpen(true);
    };

    const handleConfirmSend = async () => {
        setConfirmOpen(false);
        setLoading(true);
        try {
            const response = await sendBroadcastNotification(form);
            setResult({
                open: true,
                message: response.data.message,
                count: response.data.audienceCount
            });
            // Optional: Reset form on success
            // setForm({ ...form, title: '', body: '' });
        } catch (error: any) {
            console.error("Broadcast failed", error);
            setResult({
                open: true,
                message: error.response?.data?.message || 'Failed to send broadcast',
                count: 0
            });
        } finally {
            setLoading(false);
        }
    };

    // --- RICH TEXT HELPERS ---

    // 1. Insert Emoji
    const handleEmojiClick = (emojiData: EmojiClickData) => {
        const input = bodyInputRef.current;
        // Fallback to end of text if input ref is missing
        const start = input?.selectionStart ?? form.body.length;
        const end = input?.selectionEnd ?? form.body.length;

        // Insert emoji between the text before cursor and text after cursor
        const newBody = form.body.substring(0, start) + emojiData.emoji + form.body.substring(end);

        setForm(prev => ({ ...prev, body: newBody }));
        setEmojiAnchorEl(null);

        // Restore focus and move cursor after the new emoji
        setTimeout(() => {
            if (input) {
                const newCursorPos = start + emojiData.emoji.length;
                input.focus();
                input.setSelectionRange(newCursorPos, newCursorPos);
            }
        }, 0);
    };

    // 2. Convert to Unicode Bold (Mathematical Sans-Serif Bold)
    // This allows "Bold" text to appear in Push Notifications where HTML is not supported.
    const handleConvertToBold = () => {
        const input = bodyInputRef.current;
        if (!input) return;

        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;

        // If no text selected, do nothing
        if (start === end) return;

        const originalFullText = form.body;
        const selectedText = originalFullText.substring(start, end);

        // Helper: Get code point (handles UTF-16 surrogate pairs)
        const getCodePoint = (char: string) => char.codePointAt(0) || 0;

        // Range Definitions for Mathematical Sans-Serif Bold
        const isBoldUpper = (c: number) => c >= 0x1D5D4 && c <= 0x1D5ED;
        const isBoldLower = (c: number) => c >= 0x1D5EE && c <= 0x1D607;
        const isBoldDigit = (c: number) => c >= 0x1D7EC && c <= 0x1D7F5;

        // --- DETECT MODE ---
        // Iterate over characters properly. If we find ANY bold char first, we switch to UNBOLD mode.
        let mode = 'bold'; // Default to bolding
        for (const char of Array.from(selectedText)) {
            const c = getCodePoint(char);
            // Ignore spaces/symbols, look for the first alpha-numeric char
            if (isBoldUpper(c) || isBoldLower(c) || isBoldDigit(c)) {
                mode = 'unbold';
                break;
            }
            if ((c >= 65 && c <= 90) || (c >= 97 && c <= 122) || (c >= 48 && c <= 57)) {
                mode = 'bold';
                break;
            }
        }

        // --- TRANSFORM ---
        const transformChar = (char: string) => {
            const c = getCodePoint(char);

            if (mode === 'unbold') {
                if (isBoldLower(c)) return String.fromCodePoint(c - (0x1D5EE - 97)); // a-z
                if (isBoldUpper(c)) return String.fromCodePoint(c - (0x1D5D4 - 65)); // A-Z
                if (isBoldDigit(c)) return String.fromCodePoint(c - (0x1D7EC - 48)); // 0-9
                return char;
            } else {
                if (c >= 97 && c <= 122) return String.fromCodePoint(0x1D5EE + (c - 97)); // a-z
                if (c >= 65 && c <= 90) return String.fromCodePoint(0x1D5D4 + (c - 65)); // A-Z
                if (c >= 48 && c <= 57) return String.fromCodePoint(0x1D7EC + (c - 48)); // 0-9
                return char;
            }
        };

        // Use Array.from to correctly handle 2-byte bold characters
        const newSelection = Array.from(selectedText).map(transformChar).join('');
        const newBody = originalFullText.substring(0, start) + newSelection + originalFullText.substring(end);

        setForm(prev => ({ ...prev, body: newBody }));

        // Restore selection (Adjusting for length changes if any)
        setTimeout(() => {
            input.focus();
            // Since bold chars are length 2 and normal are length 1, the end position changes!
            // We calculate the new end based on the newSelection length.
            const newEnd = start + newSelection.length;
            input.setSelectionRange(start, newEnd);
        }, 0);
    };

    // --- MOBILE PREVIEW COMPONENT ---
    const MobilePreview = () => (
        <Paper
            elevation={4}
            sx={{
                width: 300,
                height: 580,
                borderRadius: 4,
                border: '8px solid #333',
                bgcolor: '#f5f5f5',
                mx: 'auto',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundImage: 'linear-gradient(135deg, #e0e0e0 0%, #ffffff 100%)'
            }}
        >
            {/* Notch */}
            <Box sx={{ width: 120, height: 20, bgcolor: '#333', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, mb: 2 }} />

            {/* Time */}
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold', width: '100%', px: 3 }}>
                10:45 AM
            </Typography>

            {/* Notification Card */}
            <Paper
                elevation={2}
                sx={{
                    mt: 2,
                    width: '90%',
                    borderRadius: 2,
                    bgcolor: '#fff',
                    overflow: 'hidden',
                    cursor: 'default'
                }}
            >
                {/* Header */}
                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                        width: 20, height: 20, borderRadius: 1,
                        bgcolor: form.isUrgent ? theme.palette.error.main : theme.palette.primary.main,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        {form.isUrgent ? <Warning sx={{ fontSize: 12, color: '#fff' }} /> : <NotificationsActive sx={{ fontSize: 12, color: '#fff' }} />}
                    </Box>
                    <Typography variant="caption" sx={{ flexGrow: 1, fontWeight: 500, color: '#555' }}>
                        ApnaYatri • Now
                    </Typography>
                    {form.isUrgent && <Chip label="HIGH" color="error" size="small" sx={{ height: 16, fontSize: '0.6rem' }} />}
                </Box>

                {/* Content */}
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.5 }}>
                        {form.title || "Notification Title"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                        {form.body || "This is how your message will appear to the user. Write something catchy!"}
                    </Typography>
                </Box>

                {/* Rich Media */}
                {form.imageUrl && (
                    <CardMedia
                        component="img"
                        height="120"
                        image={form.imageUrl}
                        alt="Preview"
                        sx={{ bgcolor: '#eee' }}
                        onError={(e: any) => { e.target.src = 'https://via.placeholder.com/300x120?text=Invalid+Image+URL'; }}
                    />
                )}
            </Paper>

            {/* Footer Hint */}
            <Box sx={{ position: 'absolute', bottom: 20, textAlign: 'center', opacity: 0.5 }}>
                <PhoneAndroid fontSize="large" />
                <Typography variant="caption" display="block">Android Preview</Typography>
            </Box>
        </Paper>
    );

    return (
        <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
            {/* Page Header */}
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
                Back to Dashboard
            </Button>

            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <NotificationsActive fontSize="large" color="primary" />
                Notification Composer
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
                Send bulk push notifications to Drivers or Riders. Changes update in the preview in real-time.
            </Typography>

            {/* --- MAIN FLEX LAYOUT --- */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' }, // Stack on mobile, Row on Desktop
                gap: 4,
                alignItems: 'flex-start'
            }}>

                {/* LEFT: FORM EDITOR */}
                <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
                    <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: 3 }}>

                        {/* Row 1: Target & Urgency */}
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mb: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>Target Audience</InputLabel>
                                <Select
                                    value={form.target}
                                    label="Target Audience"
                                    onChange={(e) => handleChange('target', e.target.value)}
                                >
                                    <MenuItem value="rider">👥 All Riders</MenuItem>
                                    <MenuItem value="driver">🚖 All Drivers</MenuItem>
                                    <MenuItem value="all">🌍 Everyone (Riders + Drivers)</MenuItem>
                                </Select>
                            </FormControl>

                            <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.isUrgent}
                                            onChange={(e) => handleChange('isUrgent', e.target.checked)}
                                            color="error"
                                        />
                                    }
                                    label={<Typography fontWeight={form.isUrgent ? 'bold' : 'normal'} color={form.isUrgent ? 'error' : 'inherit'}>Urgent / Alert</Typography>}
                                />
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {/* Form Fields Stack */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                fullWidth
                                label="Notification Title"
                                placeholder="e.g. 50% Off This Weekend!"
                                value={form.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Title /></InputAdornment> }}
                            />

                            {/* BODY FIELD WITH RICH TOOLS */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 0.5 }}>
                                    <Tooltip title="Convert text to Unicode Bold (Visible in Notifications)">
                                        <IconButton size="small" onClick={handleConvertToBold} color="primary">
                                            <FormatBold />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Insert Emoji">
                                        <IconButton size="small" onClick={(e) => setEmojiAnchorEl(e.currentTarget)} color="warning">
                                            <EmojiEmotions />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <TextField
                                    fullWidth
                                    inputRef={bodyInputRef}
                                    multiline
                                    rows={3}
                                    label="Message Body"
                                    placeholder="e.g. Use code RAIN20 for discounts on all rides..."
                                    value={form.body}
                                    onChange={(e) => handleChange('body', e.target.value)}
                                    // Visual Helper for Character Limit
                                    helperText={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.min((form.body.length / 140) * 100, 100)}
                                                    color={form.body.length > 140 ? "error" : "primary"}
                                                    sx={{ height: 4, borderRadius: 2 }}
                                                />
                                            </Box>
                                            <Typography variant="caption" color={form.body.length > 140 ? "error" : "textSecondary"}>
                                                {form.body.length}/140
                                            </Typography>
                                        </Box>
                                    }
                                />
                                {/* Emoji Menu */}
                                <Menu
                                    anchorEl={emojiAnchorEl}
                                    open={Boolean(emojiAnchorEl)}
                                    onClose={() => setEmojiAnchorEl(null)}
                                    TransitionComponent={Fade}
                                    sx={{ '& .MuiPaper-root': { borderRadius: 4 } }} // Rounder corners
                                >
                                    <Box sx={{ zIndex: 1000 }}>
                                        <EmojiPicker
                                            onEmojiClick={handleEmojiClick}
                                            searchDisabled={false}
                                            skinTonesDisabled
                                            previewConfig={{ showPreview: false }} // Clean look
                                            height={400}
                                            width={320}
                                        />
                                    </Box>
                                </Menu>
                            </Box>

                            <TextField
                                fullWidth
                                label="Image URL (Optional)"
                                placeholder="https://..."
                                value={form.imageUrl}
                                onChange={(e) => handleChange('imageUrl', e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><ImageIcon /></InputAdornment> }}
                            />
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Deeplink Section */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {COMMON_DEEPLINKS.map((link) => (
                                    <Chip
                                        key={link.label}
                                        label={link.label}
                                        size="small"
                                        onClick={() => handleDeeplinkChip(link.value)}
                                        clickable
                                        color={form.deeplink === link.value ? "primary" : "default"}
                                        icon={<AutoAwesome />}
                                    />
                                ))}
                            </Box>
                            <TextField
                                fullWidth
                                label="Deep Link (Action)"
                                placeholder="apnayatri://"
                                value={form.deeplink}
                                onChange={(e) => handleChange('deeplink', e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Link /></InputAdornment> }}
                                helperText="Where the user goes when they tap the notification."
                            />
                        </Box>

                        {/* Action Button */}
                        <Box>
                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                onClick={handleSendClick}
                                disabled={!form.title || !form.body || loading}
                                endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                                sx={{ py: 1.5, borderRadius: 2, fontSize: '1.1rem' }}
                            >
                                {loading ? "Sending Broadcast..." : "Send Broadcast Notification"}
                            </Button>

                            {form.target === 'all' && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    Warning: You are targeting ALL users. This cannot be undone.
                                </Alert>
                            )}
                        </Box>
                    </Paper>
                </Box>

                {/* RIGHT: LIVE PREVIEW */}
                <Box sx={{
                    width: { xs: '100%', md: 350, lg: 400 }, // Fixed width on desktop
                    flexShrink: 0,
                    position: 'sticky',
                    top: 20
                }}>
                    <Typography variant="overline" display="block" align="center" gutterBottom color="textSecondary">
                        Live Preview
                    </Typography>
                    <MobilePreview />
                </Box>

            </Box>

            {/* --- CONFIRMATION DIALOG --- */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning color="warning" /> Confirm Broadcast
                </DialogTitle>
                <DialogContent>
                    <Box>
                        <Typography>
                            You are about to send this notification to <strong>{form.target.toUpperCase()}S</strong>.
                        </Typography>
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                            <Typography variant="subtitle2">Title: {form.title}</Typography>
                            <Typography variant="body2" color="textSecondary">Urgency: {form.isUrgent ? 'HIGH' : 'Normal'}</Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmSend} variant="contained" color="primary" autoFocus>
                        Confirm & Send
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- RESULT DIALOG --- */}
            <Dialog
                open={!!result}
                onClose={() => setResult(null)}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {result?.count ? <CheckCircle color="success" /> : <Info color="error" />}
                    {result?.count ? 'Broadcast Sent' : 'Status Info'}
                </DialogTitle>
                <DialogContent>
                    <Box>
                        <Typography gutterBottom>
                            {result?.message || "Operation completed."}
                        </Typography>

                        {result?.count ? (
                            <Typography variant="h6" align="center" sx={{ mt: 2, color: 'success.main', fontWeight: 'bold' }}>
                                🚀 Sent to {result.count} devices!
                            </Typography>
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResult(null)} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default NotificationComposer;